import type { EnclosureLighting, NightLighting } from '../config/zones.config';
import type { EnclosureDefinition } from '../level/segments/SegmentDefinition';
import { clamp01, lerp, smoothstep } from '../utils/math';

/** sRGB color, 0..1 per channel (same space as Color3.FromHexString). */
export type Rgb = [number, number, number];

/** Day values of everything the zone lighting changes (from RenderingConfig). */
export interface DayLighting {
  readonly sunIntensity: number;
  readonly sunColor: Readonly<Rgb>;
  readonly environmentIntensity: number;
  readonly fogColor: Readonly<Rgb>;
  readonly fogStart: number;
  readonly fogEnd: number;
  /** Day tint of the skyline band (see skylineDayTint). */
  readonly skylineTint: Readonly<Rgb>;
}

/**
 * The skyline panorama carries its own haze, painted in a fixed colour. Tinting the band by
 * fog / painted haze makes that haze match the current fog exactly (seamless horizon) and gives the
 * silhouettes the same colour cast as the light (e.g. warm evening sun).
 */
export function skylineDayTint(fog: Readonly<Rgb>, paintedHaze: Readonly<Rgb>): Rgb {
  return [fog[0] / Math.max(paintedHaze[0], 1e-3), fog[1] / Math.max(paintedHaze[1], 1e-3), fog[2] / Math.max(paintedHaze[2], 1e-3)];
}

/** Lighting the renderer applies this frame. */
export interface LightingState {
  sunIntensity: number;
  sunColor: Rgb;
  environmentIntensity: number;
  skyLevel: number;
  skyTint: Rgb;
  skylineLevel: number;
  skylineTint: Rgb;
  fogColor: Rgb;
  fogStart: number;
  fogEnd: number;
  /** 0..1 share of the runner light. */
  runnerLight: number;
}

export function lightingState(): LightingState {
  return {
    sunIntensity: 0,
    sunColor: [1, 1, 1],
    environmentIntensity: 0,
    skyLevel: 1,
    skyTint: [1, 1, 1],
    skylineLevel: 1,
    skylineTint: [1, 1, 1],
    fogColor: [1, 1, 1],
    fogStart: 0,
    fogEnd: 0,
    runnerLight: 0,
  };
}

const WHITE: Readonly<Rgb> = [1, 1, 1];

export function hexToRgb(hex: string): Rgb {
  const value = Number.parseInt(hex.replace('#', ''), 16);
  return [((value >> 16) & 0xff) / 255, ((value >> 8) & 0xff) / 255, (value & 0xff) / 255];
}

function mixRgb(a: Readonly<Rgb>, b: Readonly<Rgb>, t: number, out: Rgb): Rgb {
  out[0] = lerp(a[0], b[0], t);
  out[1] = lerp(a[1], b[1], t);
  out[2] = lerp(a[2], b[2], t);
  return out;
}

/** Night colors decoded once per config object. */
const decoded = new WeakMap<NightLighting, { sun: Rgb; sky: Rgb; skyline: Rgb; fog: Rgb }>();
function nightColors(night: NightLighting) {
  let colors = decoded.get(night);
  if (!colors) {
    colors = { sun: hexToRgb(night.sunColor), sky: hexToRgb(night.skyTint), skyline: hexToRgb(night.skylineTint), fog: hexToRgb(night.fogColor) };
    decoded.set(night, colors);
  }
  return colors;
}

/**
 * Lighting for a night share `nightFactor` (0 = day, 1 = night) and an enclosed stretch
 * (`enclosureFactor` 0 = outside, 1 = fully inside). Day/night scale and recolor the day setup;
 * an enclosure additionally dims sun and sky light (fog and sky stay – they are seen through
 * the portals). The runner light takes the stronger of both demands.
 */
export function blendLighting(
  day: DayLighting,
  night: NightLighting,
  nightFactor: number,
  enclosure: EnclosureLighting | null,
  enclosureFactor: number,
  out: LightingState = lightingState(),
): LightingState {
  const n = clamp01(nightFactor);
  const e = enclosure ? clamp01(enclosureFactor) : 0;
  const colors = nightColors(night);
  const enclosedSun = enclosure ? lerp(1, enclosure.sunScale, e) : 1;
  const enclosedEnv = enclosure ? lerp(1, enclosure.environmentScale, e) : 1;
  out.sunIntensity = day.sunIntensity * lerp(1, night.sunScale, n) * enclosedSun;
  mixRgb(day.sunColor, colors.sun, n, out.sunColor);
  out.environmentIntensity = day.environmentIntensity * lerp(1, night.environmentScale, n) * enclosedEnv;
  out.skyLevel = lerp(1, night.skyLevel, n);
  mixRgb(WHITE, colors.sky, n, out.skyTint);
  out.skylineLevel = lerp(1, night.skylineLevel, n);
  mixRgb(day.skylineTint, colors.skyline, n, out.skylineTint);
  mixRgb(day.fogColor, colors.fog, n, out.fogColor);
  out.fogStart = lerp(day.fogStart, night.fogStart, n);
  out.fogEnd = lerp(day.fogEnd, night.fogEnd, n);
  out.runnerLight = Math.max(n * night.runnerLight, e * (enclosure?.runnerLight ?? 0));
  return out;
}

/**
 * How far the camera is inside an enclosed stretch: 0 outside, easing to 1 within `fade`
 * meters behind either portal. `localZ` = path position inside the segment.
 */
export function enclosureFactor(enclosure: EnclosureDefinition | undefined, localZ: number, fade: number): number {
  if (!enclosure || localZ < enclosure.zFrom || localZ > enclosure.zTo) return 0;
  const inside = Math.min(localZ - enclosure.zFrom, enclosure.zTo - localZ);
  return fade > 0 ? smoothstep(inside / fade) : 1;
}

/** Eased day/night progress: moves `progress` towards the target by dt / duration. */
export function stepTransition(progress: number, night: boolean, dt: number, duration: number): number {
  const step = duration > 0 ? dt / duration : 1;
  return night ? Math.min(1, progress + step) : Math.max(0, progress - step);
}

/** True if two states differ noticeably (the renderer re-applies only then). */
export function lightingChanged(a: LightingState, b: LightingState, epsilon = 1e-4): boolean {
  const scalars: (keyof LightingState)[] = ['sunIntensity', 'environmentIntensity', 'skyLevel', 'skylineLevel', 'fogStart', 'fogEnd', 'runnerLight'];
  for (const key of scalars) if (Math.abs((a[key] as number) - (b[key] as number)) > epsilon) return true;
  const colors: (keyof LightingState)[] = ['sunColor', 'skyTint', 'skylineTint', 'fogColor'];
  for (const key of colors) {
    const x = a[key] as Rgb;
    const y = b[key] as Rgb;
    if (Math.abs(x[0] - y[0]) + Math.abs(x[1] - y[1]) + Math.abs(x[2] - y[2]) > epsilon) return true;
  }
  return false;
}

export function copyLighting(from: LightingState, to: LightingState): LightingState {
  to.sunIntensity = from.sunIntensity;
  to.environmentIntensity = from.environmentIntensity;
  to.skyLevel = from.skyLevel;
  to.skylineLevel = from.skylineLevel;
  to.fogStart = from.fogStart;
  to.fogEnd = from.fogEnd;
  to.runnerLight = from.runnerLight;
  for (const key of ['sunColor', 'skyTint', 'skylineTint', 'fogColor'] as const) {
    to[key][0] = from[key][0];
    to[key][1] = from[key][1];
    to[key][2] = from[key][2];
  }
  return to;
}
